import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  Check,
  ExternalLink,
  Facebook,
  Instagram,
  Link as LinkIcon,
  Loader2,
  MessageCircle,
  MessageSquare,
  Plus,
  Power,
  RefreshCw,
  Save,
  Trash2,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { AssignClientMeta } from "@/components/assign-client-meta";
import { FixBlockerLink } from "@/components/fix-blocker-link";
import { useToast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Platform = "facebook" | "instagram";
type RuleType = "comment_to_dm" | "dm_reply";
type TriggerType = "any" | "keywords";
type MediaScope = "all" | "selected";

interface SocialAccount {
  id: string;
  platform: Platform;
  pageId: string;
  igUserId?: string | null;
  name: string;
  pageName?: string | null;
  subscribed: boolean;
  subscriptionError?: string | null;
}

interface SocialMediaItem {
  id: string;
  label: string;
  thumbnailUrl?: string;
  permalink?: string;
  type?: string;
  timestamp?: string;
}

interface AccountsResponse {
  accounts: SocialAccount[];
  syncError?: string;
}

interface AutomationRule {
  id: string;
  accountId: string;
  platform: Platform;
  name: string;
  type: RuleType;
  mediaScope: MediaScope;
  mediaIds: string[];
  triggerType: TriggerType;
  keywords: string[];
  responseText: string;
  responseLink?: string | null;
  publicReplyText?: string | null;
  active: boolean;
  sentCount: number;
  lastSentAt?: string | null;
  account?: SocialAccount | null;
}

interface FormState {
  accountId: string;
  name: string;
  type: RuleType;
  mediaScope: MediaScope;
  mediaIds: string[];
  triggerType: TriggerType;
  keywords: string;
  responseText: string;
  responseLink: string;
  publicReplyText: string;
  active: boolean;
}

const DEFAULT_FORM: FormState = {
  accountId: "",
  name: "",
  type: "comment_to_dm",
  mediaScope: "all",
  mediaIds: [],
  triggerType: "keywords",
  keywords: "LINK",
  responseText: "",
  responseLink: "",
  publicReplyText: "",
  active: true,
};

const PLATFORM_CONFIG = {
  facebook: {
    label: "Facebook",
    icon: <Facebook size={14} />,
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  },
  instagram: {
    label: "Instagram",
    icon: <Instagram size={14} />,
    color: "text-pink-400 bg-pink-500/10 border-pink-500/20",
  },
} satisfies Record<Platform, { label: string; icon: ReactNode; color: string }>;

function splitKeywords(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatDate(value: string | null | undefined, missingLabel: string, locale: string) {
  if (!value) return missingLabel;
  return new Date(value).toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function RuleTypeLabel({ type, t }: { type: RuleType; t: (k: string) => string }) {
  if (type === "comment_to_dm") {
    return (
      <span className="inline-flex items-center gap-1">
        <MessageSquare size={12} />
        {t("socialAutomations.ruleType.commentToDm")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <MessageCircle size={12} />
      {t("socialAutomations.ruleType.dmReply")}
    </span>
  );
}

export default function SocialAutomations() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);

  // When an agency owner / employee picks a client from the global selector,
  // all queries scope to that client's user — same pattern as dashboard.tsx.
  // Without this the page tried to sync the agency owner's own (often empty)
  // Meta integration and 500'd when sync failed.
  const { selectedClientUserId, selectedClientId, selectedClient } = useAgencyClients();
  const params = new URLSearchParams();
  if (selectedClientUserId) params.set("forClientUserId", selectedClientUserId);
  if (selectedClientId) params.set("forAgencyClientId", selectedClientId);
  const queryString = params.toString();
  const forUserParam = queryString ? `?${queryString}` : "";
  const forUserBody: Record<string, string> = {};
  if (selectedClientUserId) forUserBody.forClientUserId = selectedClientUserId;
  if (selectedClientId) forUserBody.forAgencyClientId = selectedClientId;

  const {
    data: accountsData,
    isLoading: accountsLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useQuery<AccountsResponse>({
    queryKey: ["/api/social-automations/accounts", selectedClientUserId ?? selectedClientId ?? ""],
    queryFn: () => apiRequest("GET", `/api/social-automations/accounts${forUserParam}`),
  });

  const { data: rulesData, isLoading: rulesLoading } = useQuery<{ rules: AutomationRule[] }>({
    queryKey: ["/api/social-automations/rules", selectedClientUserId ?? selectedClientId ?? ""],
    queryFn: () => apiRequest("GET", `/api/social-automations/rules${forUserParam}`),
  });

  // Jak wyzej (VP-326): trafia do zaleznosci useEffect, wiec musi byc stabilne.
  const accounts = useMemo(() => accountsData?.accounts ?? [], [accountsData]);
  const rules = rulesData?.rules ?? [];
  const selectedAccount = accounts.find((account) => account.id === form.accountId) ?? null;

  const resyncMutation = useMutation({
    mutationFn: () =>
      apiRequest<AccountsResponse>(
        "POST",
        `/api/social-automations/accounts/resync${forUserParam}`
      ),
    onSuccess: (result: AccountsResponse) => {
      if (result.syncError) {
        toast({
          title: t("socialAutomations.toast.partialResync"),
          description: result.syncError,
          variant: "destructive",
        });
      } else {
        toast({ title: t("socialAutomations.toast.refreshed") });
      }
      queryClient.invalidateQueries({
        queryKey: [
          "/api/social-automations/accounts",
          selectedClientUserId ?? selectedClientId ?? "",
        ],
      });
    },
    onError: (err: Error) => {
      toast({
        title: t("socialAutomations.toast.refreshFailed"),
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    },
  });

  const { data: mediaData, isLoading: mediaLoading } = useQuery<{ media: SocialMediaItem[] }>({
    queryKey: [
      "/api/social-automations/media",
      form.accountId,
      selectedClientUserId ?? selectedClientId ?? "",
    ],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/social-automations/media?accountId=${form.accountId}${queryString ? `&${queryString}` : ""}`
      ),
    enabled: !!form.accountId && form.type === "comment_to_dm" && form.mediaScope === "selected",
  });

  const media = mediaData?.media ?? [];

  useEffect(() => {
    if (!form.accountId && accounts.length > 0) {
      setForm((current) => ({ ...current, accountId: accounts[0].id }));
    }
  }, [accounts, form.accountId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...forUserBody,
        accountId: form.accountId,
        name: form.name || undefined,
        type: form.type,
        mediaScope: form.type === "comment_to_dm" ? form.mediaScope : "all",
        mediaIds:
          form.type === "comment_to_dm" && form.mediaScope === "selected" ? form.mediaIds : [],
        triggerType: form.triggerType,
        keywords: form.triggerType === "keywords" ? splitKeywords(form.keywords) : [],
        responseText: form.responseText,
        responseLink: form.responseLink || null,
        publicReplyText:
          form.type === "comment_to_dm" && form.publicReplyText.trim()
            ? form.publicReplyText.trim()
            : null,
        active: form.active,
      };
      if (editingId) {
        return apiRequest("PATCH", `/api/social-automations/rules/${editingId}`, payload);
      }
      return apiRequest("POST", "/api/social-automations/rules", payload);
    },
    onSuccess: () => {
      toast({
        title: editingId
          ? t("socialAutomations.toast.saved")
          : t("socialAutomations.toast.created"),
      });
      setEditingId(null);
      setForm((current) => ({ ...DEFAULT_FORM, accountId: current.accountId }));
      queryClient.invalidateQueries({
        queryKey: ["/api/social-automations/rules", selectedClientUserId ?? selectedClientId ?? ""],
      });
    },
    onError: (err: Error) => {
      toast({
        title: t("socialAutomations.toast.saveFailed"),
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiRequest("PATCH", `/api/social-automations/rules/${id}`, { ...forUserBody, active }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["/api/social-automations/rules", selectedClientUserId ?? selectedClientId ?? ""],
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/social-automations/rules/${id}${forUserParam}`),
    onSuccess: () => {
      toast({ title: t("socialAutomations.toast.deleted") });
      queryClient.invalidateQueries({
        queryKey: ["/api/social-automations/rules", selectedClientUserId ?? selectedClientId ?? ""],
      });
    },
  });

  const canSave = useMemo(() => {
    if (!form.accountId || !form.responseText.trim()) return false;
    if (form.triggerType === "keywords" && splitKeywords(form.keywords).length === 0) return false;
    if (
      form.type === "comment_to_dm" &&
      form.mediaScope === "selected" &&
      form.mediaIds.length === 0
    )
      return false;
    return true;
  }, [form]);

  const startEdit = (rule: AutomationRule) => {
    setEditingId(rule.id);
    setForm({
      accountId: rule.accountId,
      name: rule.name,
      type: rule.type,
      mediaScope: rule.mediaScope,
      mediaIds: rule.mediaIds ?? [],
      triggerType: rule.triggerType,
      keywords: (rule.keywords ?? []).join(", "),
      responseText: rule.responseText,
      responseLink: rule.responseLink ?? "",
      publicReplyText: rule.publicReplyText ?? "",
      active: rule.active,
    });
  };

  const toggleMedia = (mediaId: string) => {
    setForm((current) => ({
      ...current,
      mediaIds: current.mediaIds.includes(mediaId)
        ? current.mediaIds.filter((id) => id !== mediaId)
        : [...current.mediaIds, mediaId],
    }));
  };

  if (!accountsLoading && (accountsError || accountsData?.syncError)) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-white/35 gap-3 px-6">
        <Bot size={46} className="text-white/20" />
        <p className="text-sm font-medium text-white/60 text-center">
          {t("socialAutomations.error.fetchAccountsTitle")}
        </p>
        <p className="text-xs max-w-lg text-center text-white/35">
          {accountsData?.syncError || (accountsError as Error)?.message}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => resyncMutation.mutate()}
          className="border-white/[0.12] text-white/60 hover:bg-white/[0.06]"
        >
          {resyncMutation.isPending ? (
            <Loader2 size={13} className="mr-1.5 animate-spin" />
          ) : (
            <RefreshCw size={13} className="mr-1.5" />
          )}
          {t("socialAutomations.error.resync")}
        </Button>
      </div>
    );
  }

  if (!accountsLoading && accounts.length === 0) {
    const clientLabel = selectedClient?.name ? `„${selectedClient.name}"` : "ten klient";
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#ef3e29]/15 flex items-center justify-center">
          <Bot size={26} className="text-[#ef3e29]" />
        </div>
        <div className="text-center max-w-md">
          <p className="text-base font-semibold text-white">
            Automatyzacje wymagają podłączonej Meta
          </p>
          <p className="text-sm text-white/50 mt-1.5 leading-relaxed">
            {clientLabel} nie ma jeszcze podłączonego konta. Aby auto-komentarze i auto-DM działały,
            klient musi mieć w panelu agencyjnym podpiętą{" "}
            <span className="text-white/80">Stronę Facebook</span> oraz konto{" "}
            <span className="text-white/80">Instagram Professional</span>.
          </p>
        </div>
        {selectedClientId ? (
          <AssignClientMeta
            clientId={selectedClientId}
            clientName={selectedClient?.name}
            invalidateKeys={[
              ["/api/social-automations/accounts", selectedClientUserId ?? selectedClientId ?? ""],
            ]}
            onAssigned={() => resyncMutation.mutate()}
          />
        ) : (
          <ol className="text-xs text-white/55 space-y-1.5 max-w-md w-full bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
            <li>
              <span className="text-[#ef3e29] font-semibold">1.</span> Wybierz klienta w selektorze
              u góry.
            </li>
            <li>
              <span className="text-[#ef3e29] font-semibold">2.</span> Przypisz mu Stronę FB + IG
              Professional tutaj.
            </li>
          </ol>
        )}
        <div className="flex items-center gap-4">
          <Button
            size="sm"
            variant="outline"
            onClick={() => resyncMutation.mutate()}
            className="border-white/[0.12] text-white/60 hover:bg-white/[0.06]"
          >
            {resyncMutation.isPending ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : (
              <RefreshCw size={13} className="mr-1.5" />
            )}
            {t("socialAutomations.empty.refresh")}
          </Button>
          <FixBlockerLink label="Centrum Integracji" open="meta" className="text-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden bg-background">
      <div className="w-[360px] shrink-0 border-r border-white/[0.06] overflow-y-auto">
        <div className="p-4 border-b border-white/[0.06]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Bot size={16} className="text-[#ef3e29]" />
                {t("socialAutomations.title")}
              </h2>
              <p className="text-[11px] text-white/35 mt-1">{t("socialAutomations.subtitle")}</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditingId(null);
                setForm((current) => ({
                  ...DEFAULT_FORM,
                  accountId: current.accountId || accounts[0]?.id || "",
                }));
              }}
              className="h-8 px-2 text-white/45 hover:text-white"
            >
              <Plus size={14} />
            </Button>
          </div>
        </div>

        <div className="p-3 space-y-2">
          {rulesLoading && (
            <div className="flex items-center gap-2 py-8 justify-center text-white/25 text-xs">
              <Loader2 size={14} className="animate-spin" />
              {t("socialAutomations.loadingRules")}
            </div>
          )}

          {!rulesLoading && rules.length === 0 && (
            <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-4 text-center">
              <p className="text-xs text-white/45">{t("socialAutomations.noRules")}</p>
              <p className="text-[11px] text-white/25 mt-1">{t("socialAutomations.noRulesHint")}</p>
            </div>
          )}

          {rules.map((rule) => {
            const platform = PLATFORM_CONFIG[rule.platform];
            return (
              <button
                key={rule.id}
                onClick={() => startEdit(rule)}
                className={`w-full text-left rounded-lg border p-3 transition-all ${
                  editingId === rule.id
                    ? "border-[#ef3e29]/60 bg-[#ef3e29]/10"
                    : "border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.05]"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${platform.color}`}
                      >
                        {platform.icon}
                        {platform.label}
                      </span>
                      <span
                        className={`h-2 w-2 rounded-full ${rule.active ? "bg-emerald-400" : "bg-white/20"}`}
                      />
                    </div>
                    <p className="mt-2 text-xs font-medium text-white truncate">{rule.name}</p>
                    <p className="mt-1 text-[11px] text-white/35 flex items-center gap-1.5">
                      <RuleTypeLabel type={rule.type} t={t} />
                    </p>
                  </div>
                  <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => toggleMutation.mutate({ id: rule.id, active: !rule.active })}
                      className="p-1.5 rounded-md text-white/35 hover:text-white hover:bg-white/[0.06]"
                      title={
                        rule.active ? t("socialAutomations.disable") : t("socialAutomations.enable")
                      }
                    >
                      <Power size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(rule.id)}
                      className="p-1.5 rounded-md text-white/25 hover:text-red-300 hover:bg-red-500/10"
                      title={t("socialAutomations.delete")}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-[10px] text-white/30">
                  <span>{t("socialAutomations.sentCount", { count: rule.sentCount })}</span>
                  <span>
                    {t("socialAutomations.lastAt")}:{" "}
                    {formatDate(rule.lastSentAt, t("socialAutomations.never"), dateLocale)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-white">
                {editingId
                  ? t("socialAutomations.form.editTitle")
                  : t("socialAutomations.form.newTitle")}
              </h3>
              <p className="text-xs text-white/35 mt-1">{t("socialAutomations.form.subtitle")}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/35">
                {t("socialAutomations.form.activeLabel")}
              </span>
              <Switch
                checked={form.active}
                onCheckedChange={(active) => setForm((current) => ({ ...current, active }))}
                className="data-[state=checked]:bg-[#ef3e29]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-white/40">
                {t("socialAutomations.form.account")}
              </label>
              <Select
                value={form.accountId}
                onValueChange={(accountId) =>
                  setForm((current) => ({ ...current, accountId, mediaIds: [] }))
                }
              >
                <SelectTrigger className="mt-1 h-10 min-h-10 bg-white/[0.04] border-white/[0.08] text-white text-xs">
                  <SelectValue placeholder={t("socialAutomations.form.accountPlaceholder")} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-white/[0.08]">
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id} className="text-xs text-white">
                      {PLATFORM_CONFIG[account.platform].label}: {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAccount?.subscriptionError && (
                <p className="mt-1 text-[10px] text-amber-300">
                  {t("socialAutomations.form.webhookError")}: {selectedAccount.subscriptionError}
                </p>
              )}
            </div>

            <div>
              <label className="text-[11px] font-medium text-white/40">
                {t("socialAutomations.form.type")}
              </label>
              <Select
                value={form.type}
                onValueChange={(type: RuleType) =>
                  setForm((current) => ({
                    ...current,
                    type,
                    mediaScope: type === "dm_reply" ? "all" : current.mediaScope,
                  }))
                }
              >
                <SelectTrigger className="mt-1 h-10 min-h-10 bg-white/[0.04] border-white/[0.08] text-white text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-white/[0.08]">
                  <SelectItem value="comment_to_dm" className="text-xs text-white">
                    {t("socialAutomations.ruleType.commentToDm")}
                  </SelectItem>
                  <SelectItem value="dm_reply" className="text-xs text-white">
                    {t("socialAutomations.ruleType.dmReply")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-medium text-white/70">
              <MessageSquare size={14} className="text-white/35" />
              {t("socialAutomations.form.whenFire")}
            </div>

            {form.type === "comment_to_dm" && (
              <div>
                <label className="text-[11px] font-medium text-white/40">
                  {t("socialAutomations.form.postsReels")}
                </label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {(["all", "selected"] as MediaScope[]).map((scope) => (
                    <button
                      key={scope}
                      type="button"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          mediaScope: scope,
                          mediaIds: scope === "all" ? [] : current.mediaIds,
                        }))
                      }
                      className={`h-9 rounded-lg border text-xs transition-all ${
                        form.mediaScope === scope
                          ? "border-[#ef3e29]/70 bg-[#ef3e29]/10 text-white"
                          : "border-white/[0.08] bg-white/[0.03] text-white/40 hover:text-white/70"
                      }`}
                    >
                      {scope === "all"
                        ? t("socialAutomations.form.allPosts")
                        : t("socialAutomations.form.selectedPosts")}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {form.type === "comment_to_dm" && form.mediaScope === "selected" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-medium text-white/40">
                    {t("socialAutomations.form.choosePublications")}
                  </label>
                  {mediaLoading && <Loader2 size={12} className="animate-spin text-white/25" />}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
                  {media.map((item) => {
                    const selected = form.mediaIds.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => toggleMedia(item.id)}
                        className={`flex items-center gap-2 rounded-lg border p-2 text-left transition-all ${
                          selected
                            ? "border-[#ef3e29]/70 bg-[#ef3e29]/10"
                            : "border-white/[0.08] bg-muted hover:bg-white/[0.04]"
                        }`}
                      >
                        <div className="h-10 w-10 rounded-md overflow-hidden bg-white/[0.06] shrink-0">
                          {item.thumbnailUrl && (
                            <img
                              src={item.thumbnailUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] text-white/70 truncate">{item.label}</p>
                          <p className="text-[10px] text-white/25">{item.type || "post"}</p>
                        </div>
                        {selected && <Check size={14} className="text-[#ef3e29] shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                {!mediaLoading && media.length === 0 && (
                  <p className="text-[11px] text-white/30">
                    {t("socialAutomations.form.noPublications")}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="text-[11px] font-medium text-white/40">
                {t("socialAutomations.form.trigger")}
              </label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {(["any", "keywords"] as TriggerType[]).map((trigger) => (
                  <button
                    key={trigger}
                    type="button"
                    onClick={() => setForm((current) => ({ ...current, triggerType: trigger }))}
                    className={`h-9 rounded-lg border text-xs transition-all ${
                      form.triggerType === trigger
                        ? "border-[#ef3e29]/70 bg-[#ef3e29]/10 text-white"
                        : "border-white/[0.08] bg-white/[0.03] text-white/40 hover:text-white/70"
                    }`}
                  >
                    {trigger === "any"
                      ? t("socialAutomations.form.anyComment")
                      : t("socialAutomations.form.keywords")}
                  </button>
                ))}
              </div>
            </div>

            {form.triggerType === "keywords" && (
              <div>
                <label className="text-[11px] font-medium text-white/40">
                  {t("socialAutomations.form.keywordsLabel")}
                </label>
                <Input
                  value={form.keywords}
                  onChange={(e) => setForm((current) => ({ ...current, keywords: e.target.value }))}
                  placeholder="LINK, PDF, OFERTA"
                  className="mt-1 h-10 bg-white/[0.04] border-white/[0.08] text-white text-xs"
                />
              </div>
            )}
          </div>

          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-medium text-white/70">
              <LinkIcon size={14} className="text-white/35" />
              {t("socialAutomations.form.whatToSend")}
            </div>

            <div>
              <label className="text-[11px] font-medium text-white/40">
                {t("socialAutomations.form.ruleName")}
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                placeholder={t("socialAutomations.form.ruleNamePlaceholder")}
                className="mt-1 h-10 bg-white/[0.04] border-white/[0.08] text-white text-xs"
              />
            </div>

            <div>
              <label className="text-[11px] font-medium text-white/40">
                {t("socialAutomations.form.message")}
              </label>
              <Textarea
                value={form.responseText}
                onChange={(e) =>
                  setForm((current) => ({ ...current, responseText: e.target.value }))
                }
                placeholder={t("socialAutomations.form.messagePlaceholder")}
                className="mt-1 min-h-[116px] bg-white/[0.04] border-white/[0.08] text-white text-xs resize-none"
                enableDictation={false}
              />
            </div>

            <div>
              <label className="text-[11px] font-medium text-white/40">
                {t("socialAutomations.form.linkOptional")}
              </label>
              <Input
                value={form.responseLink}
                onChange={(e) =>
                  setForm((current) => ({ ...current, responseLink: e.target.value }))
                }
                placeholder="https://twojadomena.pl/oferta"
                className="mt-1 h-10 bg-white/[0.04] border-white/[0.08] text-white text-xs"
              />
            </div>

            {form.type === "comment_to_dm" && (
              <div>
                <label className="text-[11px] font-medium text-white/40">
                  {t("socialAutomations.form.publicReplyLabel")}
                </label>
                <Input
                  value={form.publicReplyText}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, publicReplyText: e.target.value }))
                  }
                  placeholder={t("socialAutomations.form.publicReplyPlaceholder")}
                  className="mt-1 h-10 bg-white/[0.04] border-white/[0.08] text-white text-xs"
                  maxLength={500}
                />
                <p className="mt-1 text-[10px] text-white/30">
                  {t("socialAutomations.form.publicReplyHint")}
                </p>
              </div>
            )}

            <div className="rounded-lg bg-muted border border-white/[0.06] p-3">
              <div className="flex items-center gap-1.5 text-[10px] text-white/30 mb-2">
                <ExternalLink size={11} />
                {t("socialAutomations.form.preview")}
              </div>
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-white/70">
                {form.responseText || t("socialAutomations.form.previewPlaceholder")}
                {form.responseLink ? `\n\n${form.responseLink}` : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pb-8">
            {editingId && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEditingId(null);
                  setForm((current) => ({ ...DEFAULT_FORM, accountId: current.accountId }));
                }}
                className="text-white/45 hover:text-white"
              >
                {t("socialAutomations.form.cancel")}
              </Button>
            )}
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!canSave || saveMutation.isPending}
              className="bg-[#ef3e29] hover:bg-[#d93522] text-white"
            >
              {saveMutation.isPending ? (
                <Loader2 size={15} className="mr-2 animate-spin" />
              ) : (
                <Save size={15} className="mr-2" />
              )}
              {editingId
                ? t("socialAutomations.form.saveChanges")
                : t("socialAutomations.form.create")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
